import waitForDomContentLoaded from "./utils/waitForDomContentLoaded";

/**
 * Shortcut for document.querySelector that works exactly like it but returns `false` if no element is found.
 * Automatically waits for the DOM to be ready.
 *
 * @param {string} selector - The CSS selector to match the element.
 * @returns {Promise<HTMLElement | false>} The found HTML element or `false` if no element is found.
 *
 * @example
 * // Querying selector and waiting for the DOM to be ready
 * const elem = await qry('#my-element');
 * if (elem) {
 *   console.log('Element found:', elem);
 * } else {
 *   console.log('Element not found');
 * }
 */
export async function qry(selector: string): Promise<HTMLElement | false> {
    await waitForDomContentLoaded();
    const foundElem = document.querySelector<HTMLElement>(selector);
    return foundElem ? foundElem : false;
}

/**
 * Shortcut for document.querySelectorAll that works exactly like it but returns `false` if no elements are found.
 * Automatically waits for the DOM to be ready.
 *
 * @param {string} selector - The CSS selector to match the elements.
 * @returns {Promise<NodeListOf<HTMLElement> | false>} A NodeList of found HTML elements or `false` if no elements are found.
 *
 * @example
 * // Querying selector and waiting for the DOM to be ready
 * const elems = await qryAll('.list-item');
 * if (elems) {
 *   elems.forEach(elem => console.log('Element found:', elem));
 * } else {
 *   console.log('No elements found');
 * }
 */
export async function qryAll(selector: string): Promise<NodeListOf<HTMLElement> | false> {
    await waitForDomContentLoaded();
    const foundElems = document.querySelectorAll<HTMLElement>(selector);
    return foundElems.length > 0 ? foundElems : false;
}

/**
 * Shortcut for document.getElementById that works exactly like it but returns `false` if no element is found.
 * Automatically waits for the DOM to be ready.
 *
 * @param {string} id - The id of the element to find.
 * @returns {Promise<HTMLElement | false>} The found HTML element or `false` if no element is found.
 *
 * @example
 * // Querying ID and waiting for the DOM to be ready
 * const elem = await id('header');
 * if (elem) {
 *   console.log('Element found:', elem);
 * } else {
 *   console.log('Element not found');
 * }
 */
export async function qryById(id: string): Promise<HTMLElement | false> {
    await waitForDomContentLoaded();
    const foundElem = document.getElementById(id) as HTMLElement | null;
    return foundElem ? foundElem : false;
}

/**
 * Extends HTMLElement prototype to support 'on' method.
 *
 * @param {string} event - The event type to listen for.
 * @param {EventListenerOrEventListenerObject} callback - The callback function to execute when the event is triggered.
 * @returns {HTMLElement} The element itself to allow method chaining.
 */
/**
 * Extends HTMLElement prototype to support 'on' method.
 *
 * @param {string} event - The event type to listen for.
 * @param {EventListenerOrEventListenerObject} callback - The callback function to execute when the event is triggered.
 * @returns {HTMLElement} The element itself to allow method chaining.
 */
HTMLElement.prototype.on = function (this: HTMLElement, event: string, callback: EventListenerOrEventListenerObject): HTMLElement {
    this.addEventListener(event, callback);
    return this;
};

/**
 * Extends NodeList prototype to support 'on' method.
 *
 * @param {string} event - The event type to listen for.
 * @param {EventListenerOrEventListenerObject} callback - The callback function to execute when the event is triggered.
 * @returns {NodeListOf<HTMLElement>} The NodeList itself to allow method chaining.
 */
NodeList.prototype.on = function (this: NodeListOf<HTMLElement>, event: string, callback: EventListenerOrEventListenerObject): NodeListOf<HTMLElement> {
    this.forEach(element => element.addEventListener(event, callback));
    return this;
};

/**
 * Checks if an element has a specific class.
 *
 * @param {string} className - The class name to check for.
 * @returns {boolean} `true` if the class is present, otherwise `false`.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem && elem.hasClass('active')) {
 *   console.log('Element has the class active');
 * }
 */
HTMLElement.prototype.hasClass = function (this: HTMLElement, className: string): boolean {
    return this.classList.contains(className);
};

/**
 * Adds a class to an element.
 *
 * @param {string} className - The class name to add.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.addClass('active');
 * }
 */
HTMLElement.prototype.addClass = function (this: HTMLElement, className: string): HTMLElement {
    this.classList.add(className);
    return this;
};

/**
 * Removes a class from an element.
 *
 * @param {string} className - The class name to remove.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.removeClass('active');
 * }
 */
/**
 * Removes a class from an element.
 *
 * @param {string} className - The class name to remove.
 * @returns {HTMLElement} The element itself to allow method chaining.
 */
HTMLElement.prototype.removeClass = function (this: HTMLElement, className: string): HTMLElement {
    this.classList.remove(className);
    return this;
};

/**
 * Toggles a class on an element.
 *
 * @param {string} className - The class name to toggle.
 * @param {boolean} [force] - If `true`, the class will be added; if `false`, it will be removed.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.toggleClass('active');
 * }
 */
HTMLElement.prototype.toggleClass = function (this: HTMLElement, className: string, force?: boolean): HTMLElement {
    this.classList.toggle(className, force);
    return this;
};

/**
 * Sets an attribute on an element.
 *
 * @param {string} name - The attribute name.
 * @param {string} value - The attribute value.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.setAttribute('data-role', 'admin');
 * }
 */
HTMLElement.prototype.setAttribute = function (this: HTMLElement, name: string, value: string): HTMLElement {
    this.setAttribute(name, value);
    return this;
};

/**
 * Gets an attribute from an element.
 *
 * @param {string} name - The attribute name.
 * @returns {string | null} The attribute value, or `null` if the attribute does not exist.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   console.log(elem.getAttribute('data-role')); // Outputs the value of the 'data-role' attribute
 * }
 */
HTMLElement.prototype.getAttribute = function (this: HTMLElement, name: string): string | null {
    return this.getAttribute(name);
};

/**
 * Removes an attribute from an element.
 *
 * @param {string} name - The attribute name.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.removeAttribute('data-role');
 * }
 */
HTMLElement.prototype.removeAttribute = function (this: HTMLElement, name: string): HTMLElement {
    this.removeAttribute(name);
    return this;
};

/**
 * Appends content to an element.
 *
 * @param {Node | string} content - The content to append.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.append('<span>New Content</span>');
 * }
 */
HTMLElement.prototype.append = function (this: HTMLElement, content: Node | string): HTMLElement {
    if (typeof content === 'string') {
        this.insertAdjacentHTML('beforeend', content);
    } else {
        this.appendChild(content);
    }
    return this;
};

/**
 * Prepends content to an element.
 *
 * @param {Node | string} content - The content to prepend.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.prepend('<span>New Content</span>');
 * }
 */
HTMLElement.prototype.prepend = function (this: HTMLElement, content: Node | string): HTMLElement {
    if (typeof content === 'string') {
        this.insertAdjacentHTML('afterbegin', content);
    } else {
        this.prepend(content);
    }
    return this;
};

/**
 * Removes all child elements from an element.
 *
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.empty();
 * }
 */
HTMLElement.prototype.empty = function (this: HTMLElement): HTMLElement {
    while (this.firstChild) {
        this.removeChild(this.firstChild);
    }
    return this;
};

/**
 * Fades in an element with animation.
 *
 * @param {number} duration - The duration of the fade-in effect in milliseconds.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.fadeIn(500);
 * }
 */
HTMLElement.prototype.fadeIn = function (this: HTMLElement, duration: number): HTMLElement {
    this.style.opacity = '0';
    this.style.display = '';
    this.style.transition = `opacity ${duration}ms`;
    requestAnimationFrame(() => {
        this.style.opacity = '1';
    });
    return this;
};

/**
 * Fades out an element with animation.
 *
 * @param {number} duration - The duration of the fade-out effect in milliseconds.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.fadeOut(500);
 * }
 */
HTMLElement.prototype.fadeOut = function (this: HTMLElement, duration: number): HTMLElement {
    this.style.opacity = '1';
    this.style.transition = `opacity ${duration}ms`;
    requestAnimationFrame(() => {
        this.style.opacity = '0';
        setTimeout(() => {
            this.style.display = 'none';
        }, duration);
    });
    return this;
};

/**
 * Scrolls an element to the top.
 *
 * @param {number} [duration=300] - The duration of the scroll effect in milliseconds.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.scrollToTop();
 * }
 */
HTMLElement.prototype.scrollToTop = function (this: HTMLElement, duration: number = 300): HTMLElement {
    const start = this.scrollTop;
    const startTime = performance.now();

    const scroll = (timestamp: number) => {
        const progress = Math.min((timestamp - startTime) / duration, 1);
        this.scrollTop = start * (1 - progress);
        if (progress < 1) {
            requestAnimationFrame(scroll);
        }
    };

    requestAnimationFrame(scroll);
    return this;
};

/**
 * Scrolls an element to the bottom.
 *
 * @param {number} [duration=300] - The duration of the scroll effect in milliseconds.
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   elem.scrollToBottom();
 * }
 */
HTMLElement.prototype.scrollToBottom = function (this: HTMLElement, duration: number = 300): HTMLElement {
    const start = this.scrollTop;
    const end = this.scrollHeight - this.clientHeight;
    const startTime = performance.now();

    const scroll = (timestamp: number) => {
        const progress = Math.min((timestamp - startTime) / duration, 1);
        this.scrollTop = start + (end - start) * progress;
        if (progress < 1) {
            requestAnimationFrame(scroll.bind(this));
        }
    }

    requestAnimationFrame(scroll.bind(this));
    return this;
};

/**
 * Clones an element, with or without children.
 *
 * @param {boolean} [deep=false] - Whether to clone the element's children.
 * @returns {HTMLElement} The cloned element.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   const clone = elem.clone(true); // deep clone
 *   document.body.append(clone);
 * }
 */
HTMLElement.prototype.clone = function (this: HTMLElement, deep: boolean = false): HTMLElement {
    return this.cloneNode(deep) as HTMLElement;
};

/**
 * Finds the closest ancestor element that matches the specified selector.
 *
 * @param {string} selector - The CSS selector to match the ancestor element.
 * @returns {HTMLElement | null} The closest ancestor element that matches the selector, or `null` if none is found.
 *
 * @example
 * const elem = qry('.my-element');
 * if (elem) {
 *   const ancestor = elem.closestAncestor('.parent-class');
 *   console.log(ancestor);
 * }
 */
HTMLElement.prototype.closestAncestor = function (this: HTMLElement, selector: string): HTMLElement | null {
    return this.closest(selector);
};

/**
 * Finds child elements that match the specified selector.
 *
 * @param {string} selector - The CSS selector to match the child elements.
 * @returns {NodeListOf<HTMLElement>} A NodeList of found child elements.
 *
 * @example
 * const elem = qry('#parent');
 * if (elem) {
 *   const children = elem.findChildren('.child');
 *   children.forEach(child => console.log(child));
 * }
 */
HTMLElement.prototype.findChildren = function (this: HTMLElement, selector: string): NodeListOf<HTMLElement> {
    return this.querySelectorAll(selector);
};

/**
 * Removes all child elements from an element.
 *
 * @returns {HTMLElement} The element itself to allow method chaining.
 *
 * @example
 * const elem = qry('#parent');
 * if (elem) {
 *   elem.removeChildren();
 * }
 */
HTMLElement.prototype.removeChildren = function (this: HTMLElement): HTMLElement {
    while (this.firstChild) {
        this.removeChild(this.firstChild);
    }
    return this;
};
